import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, ElementRef, inject, ViewChild } from '@angular/core';
import { ChatMessage } from '../../models/chat.models';
import { ChatStore } from '../../services/chat.store';
import { dayLabel, isSameDay, shouldGroupWithPrevious } from '../../utils/chat-date.util';
import { ChatMessageComponent } from '../chat-message/chat-message.component';

/** A message plus the presentation decisions made once, here, instead of inside each bubble. */
interface RenderedMessage {
  message: ChatMessage;
  /** Set when this message starts a new day — renders the centred "Today" / "23 May" divider. */
  daySeparator: string | null;
  /** Same sender, minutes apart: hide the avatar and name. */
  grouped: boolean;
}

/**
 * The scrolling transcript.
 *
 * Owns four behaviours that are easy to get wrong and annoying when they are:
 *
 * - **Stick to the bottom, but only if you were already there.** A new message must not yank
 *   the view down while someone is reading scrollback.
 * - **Anything you send pulls the view down regardless.** You just pressed Enter; being left
 *   staring at last week's messages while your own reply lands off-screen is never what you meant.
 * - **Load older on scroll-up without losing your place** — the scroll offset is restored after
 *   the prepend, so the viewport doesn't jump.
 * - **Count what arrived while you were away** and offer it as a button, rather than silently
 *   moving the page or leaving the user to guess whether anything happened.
 */
@Component({
  selector: 'app-chat-message-list',
  standalone: true,
  imports: [CommonModule, ChatMessageComponent],
  templateUrl: './chat-message-list.component.html',
  styleUrls: ['./chat-message-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageListComponent {
  readonly store = inject(ChatStore);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('scroller') private scroller?: ElementRef<HTMLElement>;

  /** How close to the bottom still counts as "at the bottom". */
  private static readonly STICK_THRESHOLD_PX = 120;

  private stickToBottom = true;
  private pendingRestore: { height: number; top: number } | null = null;
  private lastConversationId: string | null = null;

  /** How many messages have landed below the fold since the user scrolled away. */
  unseenCount = 0;

  /** Length and newest key of the last render, so an append can be told from a prepend. */
  private lastCount = 0;
  private lastNewestKey: string | null = null;

  /** The message list with day separators and sender grouping already resolved. */
  readonly rendered = computed<RenderedMessage[]>(() => {
    const messages = this.store.activeMessages();
    return messages.map((message, index) => {
      const previous = messages[index - 1];
      const newDay = !previous || !isSameDay(previous.created_at, message.created_at);
      return {
        message,
        daySeparator: newDay ? dayLabel(message.created_at) : null,
        grouped: !newDay && shouldGroupWithPrevious(message, previous),
      };
    });
  });

  constructor() {
    // One effect drives every scroll decision: conversation switches jump, appends stick or
    // count, and an older page restores the previous offset.
    effect(() => {
      const conversationId = this.store.activeId();
      const rendered = this.rendered();
      const switched = conversationId !== this.lastConversationId;
      this.lastConversationId = conversationId;

      const newest = rendered[rendered.length - 1]?.message ?? null;
      const newestKey = newest ? newest.client_msg_id || newest.message_id : null;
      const delta = rendered.length - this.lastCount;

      // An append moved the *newest* message; a prepend (an older page) grew the list without
      // touching it, and `pendingRestore` marks that case outright.
      const appended = !switched && delta > 0 && newestKey !== this.lastNewestKey && !this.pendingRestore;
      const isMine = !!newest && newest.sender?.user_id === this.store.myId;

      this.lastCount = rendered.length;
      this.lastNewestKey = newestKey;

      if (appended && !isMine && !this.stickToBottom) {
        this.unseenCount += delta;
        // OnPush, and this runs off a socket frame rather than a DOM event, so the badge would
        // otherwise not repaint until something else happened to trigger a check.
        this.cdr.markForCheck();
      }

      // Read after the DOM has the new rows.
      queueMicrotask(() => {
        if (switched) {
          this.resetToBottom('auto');
          return;
        }
        if (this.pendingRestore) {
          this.restoreAfterPrepend();
          return;
        }
        // Your own message always wins: you sent it, so you get taken to it.
        if (appended && isMine) {
          this.resetToBottom('smooth');
          return;
        }
        if (this.stickToBottom && rendered.length) {
          this.scrollToBottom('auto');
        }
      });
    });

    // The typing line sits outside the scroller so it survives scrolling up, which means it also
    // *shortens* the scroller each time it appears. Left alone, the newest message would slide
    // under the fold by exactly the height of that line every time somebody started typing, and
    // back up again when they stopped — a twitch on every keystroke of the person you are talking
    // to. Re-pin instead, but only for a reader who was already at the bottom.
    effect(() => {
      this.store.activeTypingLabel();
      queueMicrotask(() => {
        if (this.stickToBottom) {
          this.scrollToBottom('auto');
        }
      });
    });
  }

  /** Back to the newest message, with nothing outstanding. */
  private resetToBottom(behavior: ScrollBehavior): void {
    this.stickToBottom = true;
    this.unseenCount = 0;
    this.scrollToBottom(behavior);
    this.cdr.markForCheck();
  }

  onScroll(): void {
    const el = this.scroller?.nativeElement;
    if (!el) {
      return;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.stickToBottom = distanceFromBottom <= ChatMessageListComponent.STICK_THRESHOLD_PX;

    // Scrolling back down by hand counts as catching up, exactly as pressing the button would.
    if (this.stickToBottom && this.unseenCount) {
      this.unseenCount = 0;
      this.store.markReadNow();
    }

    if (el.scrollTop < 80 && this.store.activeHasMore() && !this.store.loadingOlder()) {
      // Remember where we were so the prepend doesn't teleport the viewport.
      this.pendingRestore = { height: el.scrollHeight, top: el.scrollTop };
      this.store.loadOlder();
    }
  }

  private restoreAfterPrepend(): void {
    const el = this.scroller?.nativeElement;
    const snapshot = this.pendingRestore;
    this.pendingRestore = null;
    if (!el || !snapshot) {
      return;
    }
    el.scrollTop = snapshot.top + (el.scrollHeight - snapshot.height);
  }

  scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    const el = this.scroller?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollTo({ top: el.scrollHeight, behavior });
  }

  jumpToLatest(): void {
    this.resetToBottom('smooth');
    // Everything below the fold has now been looked at, so the badge should clear with it.
    this.store.markReadNow();
  }

  /** Shown whenever the user has scrolled away from the newest message. */
  get showJumpButton(): boolean {
    return !this.stickToBottom;
  }

  /** "3 new messages" / "1 new message", or '' when the button is just a way back down. */
  get unseenLabel(): string {
    if (!this.unseenCount) {
      return '';
    }
    return this.unseenCount === 1 ? '1 new message' : `${this.unseenCount} new messages`;
  }

  trackByMessage = (_: number, entry: RenderedMessage) => entry.message.client_msg_id || entry.message.message_id;
}
